// /app/frontend/src/components/admin/PhotoGalleryModal.jsx
// Lightbox component for viewing job photos

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, 
  Download, Trash2, Camera, Image as ImageIcon, SplitSquareVertical 
} from 'lucide-react';
import { toast } from 'sonner';
import PhotoComparisonSlider from './PhotoComparisonSlider';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PhotoGalleryModal({ 
  isOpen, 
  onClose, 
  eventId, 
  eventTitle,
  canDelete = false 
}) {
  const [photos, setPhotos] = useState({ before: [], after: [] });
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [showComparison, setShowComparison] = useState(false);

  const token = localStorage.getItem('apollo_token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (isOpen && eventId) {
      fetchPhotos();
    }
  }, [isOpen, eventId]);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/api/schedule/${eventId}/photos`, { headers });
      setPhotos({
        before: response.data.before_photos || [],
        after: response.data.after_photos || []
      });
    } catch (error) {
      console.error('Failed to fetch photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (photoId) => {
    if (!confirm('Delete this photo?')) return;
    
    try {
      await axios.delete(`${API}/api/schedule/photos/${photoId}`, { headers });
      toast.success('Photo deleted');
      fetchPhotos();
      if (selectedPhoto?.id === photoId) {
        setLightboxOpen(false);
        setSelectedPhoto(null);
      }
    } catch (error) {
      toast.error('Failed to delete photo');
    }
  };

  const openLightbox = (photo) => {
    setSelectedPhoto(photo);
    setZoom(1);
    setLightboxOpen(true);
  };

  const navigatePhoto = (direction) => {
    const allPhotos = [...photos.before, ...photos.after];
    const currentIndex = allPhotos.findIndex(p => p.id === selectedPhoto?.id);
    let newIndex = currentIndex + direction;
    
    if (newIndex < 0) newIndex = allPhotos.length - 1;
    if (newIndex >= allPhotos.length) newIndex = 0;
    
    setSelectedPhoto(allPhotos[newIndex]);
    setZoom(1);
  };

  const PhotoGrid = ({ title, photoList, type }) => (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3">
        {type === 'before' ? <Camera className="h-4 w-4" /> : <ImageIcon className="h-4 w-4" />}
        <h4 className="font-medium">{title}</h4>
        <Badge variant="outline">{photoList.length}</Badge>
      </div>
      
      {photoList.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg text-gray-500">
          <p>No {type} photos uploaded</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {photoList.map(photo => (
            <div 
              key={photo.id} 
              className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 cursor-pointer group"
              onClick={() => openLightbox(photo)}
            >
              <img 
                src={`${API}/api/schedule/photos/${photo.id}`}
                alt={photo.original_filename || 'Job photo'}
                className="w-full h-full object-cover transition-transform group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <ZoomIn className="h-6 w-6 text-white" />
              </div>
              {canDelete && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleDelete(photo.id); }}
                  className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <>
      {/* Main Gallery Modal */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Camera className="h-5 w-5" />
              Job Photos - {eventTitle || 'Job'}
            </DialogTitle>
          </DialogHeader>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div>
            </div>
          ) : (
            <div className="py-4">
              {/* Compare Button */}
              {photos.before.length > 0 && photos.after.length > 0 && (
                <div className="mb-4 text-center">
                  <Button 
                    onClick={() => setShowComparison(true)}
                    className="bg-gradient-to-r from-blue-600 to-green-600 hover:from-blue-700 hover:to-green-700"
                    data-testid="compare-photos-btn"
                  >
                    <SplitSquareVertical className="h-4 w-4 mr-2" />
                    Compare Before & After
                  </Button>
                </div>
              )}
              
              <PhotoGrid title="Before Photos" photoList={photos.before} type="before" />
              <PhotoGrid title="After Photos" photoList={photos.after} type="after" />
              
              {photos.before.length === 0 && photos.after.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Camera className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No photos uploaded for this job</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Lightbox Viewer */}
      {lightboxOpen && selectedPhoto && (
        <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center">
          {/* Close button */}
          <button 
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white z-10"
          >
            <X className="h-8 w-8" />
          </button>

          {/* Navigation */}
          <button 
            onClick={() => navigatePhoto(-1)}
            className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white"
          >
            <ChevronLeft className="h-10 w-10" />
          </button>
          <button 
            onClick={() => navigatePhoto(1)}
            className="absolute right-4 top-1/2 -translate-y-1/2 p-2 text-white/80 hover:text-white"
          >
            <ChevronRight className="h-10 w-10" />
          </button>

          {/* Image */}
          <div className="max-w-[90vw] max-h-[85vh] overflow-auto">
            <img 
              src={`${API}/api/schedule/photos/${selectedPhoto.id}`}
              alt={selectedPhoto.original_filename || 'Photo'}
              className="max-w-full max-h-[85vh] object-contain transition-transform"
              style={{ transform: `scale(${zoom})` }}
            />
          </div>

          {/* Bottom toolbar */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 bg-black/60 px-4 py-2 rounded-full">
            <Badge className={selectedPhoto.photo_type === 'before' ? 'bg-blue-600' : 'bg-green-600'}>
              {selectedPhoto.photo_type === 'before' ? 'Before' : 'After'}
            </Badge>
            <span className="text-white/80 text-sm">
              {selectedPhoto.uploaded_by_name}
            </span>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setZoom(Math.max(0.5, zoom - 0.25))}
                className="p-1 text-white/80 hover:text-white"
              >
                <ZoomOut className="h-5 w-5" />
              </button>
              <span className="text-white/80 text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
              <button 
                onClick={() => setZoom(Math.min(3, zoom + 0.25))}
                className="p-1 text-white/80 hover:text-white"
              >
                <ZoomIn className="h-5 w-5" />
              </button>
            </div>
            <a 
              href={`${API}/api/schedule/photos/${selectedPhoto.id}`}
              download={selectedPhoto.original_filename}
              className="p-1 text-white/80 hover:text-white"
            >
              <Download className="h-5 w-5" />
            </a>
            {canDelete && (
              <button 
                onClick={() => handleDelete(selectedPhoto.id)}
                className="p-1 text-red-400 hover:text-red-300"
              >
                <Trash2 className="h-5 w-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Photo Comparison Slider */}
      <PhotoComparisonSlider
        isOpen={showComparison}
        onClose={() => setShowComparison(false)}
        beforePhotos={photos.before}
        afterPhotos={photos.after}
        eventTitle={eventTitle}
      />
    </>
  );
}
